import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
    Button,
    Card,
    CardActions,
    CardContent,
    Container,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { useState } from 'react';
import {
    Form,
    Labeled,
    TextField,
    TextInput,
    useDataProvider,
    useGetIdentity,
    useNotify,
    PasswordInput
} from 'react-admin';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useFormState } from 'react-hook-form';
import { UserProfileForm } from '@/types';
import { CrmDataProvider } from '@/providers/types';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';

export const SettingsPage = () => {
    const [isEditMode, setEditMode] = useState(false);
    const [isEditPasswordMode, setEditPasswordMode] = useState(false);
    const { identity, refetch: refetchIdentity } = useGetIdentity();
    const dataProvider = useDataProvider<CrmDataProvider>();
    const { data, refetch: refetchUser } = useQuery({
        queryKey: ['users/me'],
        queryFn: () => dataProvider.getMe(),
        enabled: !!identity,
    });
    const notify = useNotify();

    const { mutate } = useMutation({
        mutationKey: ['users/me/update'],
        mutationFn: async (data: UserProfileForm & { new_password?: string; confirm_password?: string }) => {
            if (!identity) {
                throw new Error('Record not found');
            }
            return dataProvider.updateMe(data, isEditPasswordMode);
        },
        onSuccess: () => {
            refetchIdentity();
            refetchUser();
            setEditMode(false);
            notify('Your profile has been updated');
        },
        onError: _ => {
            console.log(_);
            notify('An error occurred. Please try again', {
                type: 'error',
            });
        },
    });

    if (!identity) return null;

    const handleOnSubmit = async (values: any) => {
        mutate(values);
    };

    const validateUserUpdate = (values:
        { new_password?: string; confirm_password?: string }
    ) => {
        const errors: { [key: string]: string } = {};

        if (isEditPasswordMode){
            if (!values.new_password) {
                errors.new_password = 'New password is required';
            }
            if (values.new_password && values.new_password !== values.confirm_password) {
                errors.confirm_password = 'The two passwords must match';
            }
            if (values.new_password && values.new_password.length < 8) {
                errors.new_password = 'Password must be at least 8 characters long';
            }
            if (values.new_password && values.new_password.length > 20) {
                errors.new_password = 'Password must be at most 20 characters long';
            }
            if (values.new_password && !/[0-9]/.test(values.new_password)) {
                errors.new_password = 'Password must contain at least one number';
            }
            if (values.new_password && !/[A-Z]/.test(values.new_password)) {
                errors.new_password = 'Password must contain at least one uppercase letter';
            }
            if (values.new_password && !/[a-zA-Z]/.test(values.new_password)) {
                errors.new_password = 'Password must contain at least one letter';
            }
        }

        return errors;
    };

    return (
        <Container maxWidth="sm" sx={{ mt: 4 }}>
            <Form onSubmit={handleOnSubmit} validate={validateUserUpdate} record={data}>
                <SettingsForm
                    isEditMode={isEditMode}
                    setEditMode={setEditMode}
                    isEditPasswordMode={isEditPasswordMode}
                    setEditPasswordMode={setEditPasswordMode}
                />
            </Form>
        </Container>
    );
};

const SettingsForm = ({
    isEditMode,
    setEditMode,
    isEditPasswordMode,
    setEditPasswordMode,
}: {
    isEditMode: boolean;
    setEditMode: (value: boolean) => void;
    isEditPasswordMode: boolean;
    setEditPasswordMode: (value: boolean) => void;
}) => {
    const { identity } = useGetIdentity();
    const { isDirty } = useFormState();

    if (!identity) return null;

    const equalToPassword = (value: any, allValues: { [key: string]: any }) => {
        if (value !== allValues.new_password) {
            return 'The two passwords must match';
        }
    }

    return (
        <Stack gap={4}>
            <Card>
                <CardContent>
                    <Stack
                        mb={2}
                        direction="row"
                        justifyContent="space-between"
                    >
                        <Typography variant="h5" color="textSecondary">
                            My info
                        </Typography>
                    </Stack>
                    <Stack gap={2} mb={2}>
                        <TextRender
                            source="name"
                            isEditMode={isEditMode}
                        />
                        <TextRender source="email" isEditMode={isEditMode} />
                    </Stack>
                    {isEditMode && !isEditPasswordMode && (
                        <>
                            <Button
                                variant="outlined"
                                onClick={() => setEditPasswordMode(!isEditPasswordMode)}
                            >
                                Change password
                            </Button>
                        </>
                    )}
                    {isEditMode && isEditPasswordMode && (
                        <Stack gap={2}>
                            <PasswordInput source="new_password" />
                            <PasswordInput source="confirm_password" validate={equalToPassword} />
                            <Button
                                variant="outlined"
                                onClick={() => setEditPasswordMode(!isEditPasswordMode)}
                            >
                                Cancel
                            </Button>
                        </Stack>
                    )}
                </CardContent>

                <CardActions
                    sx={{
                        paddingX: 2,
                        background: theme => theme.palette.background.default,
                        justifyContent: isEditMode
                            ? 'space-between'
                            : 'flex-end',
                    }}
                >
                    {isEditMode && (
                        <Button
                            variant="contained"
                            type="submit"
                            disabled={!isDirty}
                            hidden={isEditMode}
                        >
                            Save
                        </Button>
                    )}
                    <Button
                        variant="text"
                        size="small"
                        startIcon={
                            isEditMode ? <VisibilityIcon /> : <EditIcon />
                        }
                        onClick={() => setEditMode(!isEditMode)}
                    >
                        {isEditMode ? 'Show' : 'Edit'}
                    </Button>
                </CardActions>
            </Card>
            {import.meta.env.VITE_INBOUND_EMAIL && (
                <Card>
                    <CardContent>
                        <Stack gap={2} justifyContent="space-between">
                            <Typography variant="h5" color="textSecondary">
                                Inboud email
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                You can start sending emails to your server's
                                inbound email address, e.g. by adding it to the
                                <b> Cc: </b> field. MRT Dashboard will process the
                                emails and add notes to the corresponding
                                contacts.
                            </Typography>
                            <CopyPaste />
                        </Stack>
                    </CardContent>
                </Card>
            )}
        </Stack>
    );
};

const TextRender = ({
    source,
    isEditMode,
}: {
    source: string;
    isEditMode: boolean;
}) => {
    if (isEditMode) {
        return <TextInput source={source} helperText={false} />;
    }
    return (
        <Labeled sx={{ mb: 0 }}>
            <TextField source={source} />
        </Labeled>
    );
};

const CopyPaste = () => {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        setCopied(true);
        navigator.clipboard.writeText(import.meta.env.VITE_INBOUND_EMAIL);
        setTimeout(() => {
            setCopied(false);
        }, 1500);
    };
    return (
        <Tooltip placement="top" title={copied ? 'Copied!' : 'Copy'}>
            <Button
                onClick={handleCopy}
                sx={{
                    textTransform: 'none',
                    justifyContent: 'space-between',
                }}
                endIcon={<ContentCopyIcon />}
            >
                {import.meta.env.VITE_INBOUND_EMAIL}
            </Button>
        </Tooltip>
    );
};

SettingsPage.path = '/settings';
